import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Building2, Loader2, CheckCircle, AlertCircle, Trash2 } from "lucide-react";

interface LookupResult {
  contact_name: string | null;
  contact_title: string | null;
  contact_address: string | null;
  entity_type: string | null;
  status: string | null;
  source: string | null;
  confidence: string | null;
  notes: string | null;
}

interface BusinessLookupDialogProps {
  record: {
    id: string;
    business_name: string | null;
    first_name: string | null;
    last_name: string | null;
    origin_state: string | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (recordId: string, contactName: string) => void;
  onDelete?: (recordId: string) => void;
}

export function BusinessLookupDialog({ record, open, onOpenChange, onApply, onDelete }: BusinessLookupDialogProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('lookup-business-entity', {
        body: {
          business_name: record.business_name,
          state: record.origin_state,
        },
      });

      if (fnError) throw new Error(fnError.message);
      if (!data?.success) throw new Error(data?.error || 'Lookup failed');

      setResult(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to look up business entity');
      toast.error('Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!result?.contact_name) return;
    onApply(record.id, result.contact_name);
    onOpenChange(false);
    setResult(null);
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from("mailing_records")
        .delete()
        .eq("id", record.id);

      if (error) throw error;
      toast.success(`Deleted "${record.business_name || record.first_name}"`);
      onDelete?.(record.id);
      onOpenChange(false);
      setResult(null);
    } catch (err) {
      toast.error("Failed to delete record");
    }
  };

  const confidenceColor = (c: string | null) => {
    if (c === 'high') return 'default';
    if (c === 'medium') return 'secondary';
    return 'destructive';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Business Entity Lookup
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-3 rounded-md text-sm space-y-1">
            <p><span className="text-muted-foreground">Business:</span> <strong>{record.business_name}</strong></p>
            <p><span className="text-muted-foreground">Current Name:</span> {record.first_name} {record.last_name}</p>
            <p><span className="text-muted-foreground">State:</span> {record.origin_state || 'Unknown'}</p>
          </div>

          {!result && !loading && !error && (
            <Button onClick={handleLookup} className="w-full">
              <Building2 className="h-4 w-4 mr-2" />
              Search for Registered Agent / Member
            </Button>
          )}

          {loading && (
            <div className="text-center py-6">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-sm text-muted-foreground mt-2">Researching entity records...</p>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 p-3 rounded-md text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-destructive">Lookup Failed</p>
                <p className="text-muted-foreground">{error}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleLookup}>
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary" />
                <span className="font-medium">Results Found</span>
                {result.confidence && (
                  <Badge variant={confidenceColor(result.confidence)}>
                    {result.confidence} confidence
                  </Badge>
                )}
              </div>

              <div className="border rounded-md divide-y text-sm overflow-hidden">
                <Row label="Contact Name" value={result.contact_name} highlight />
                <Row label="Title/Role" value={result.contact_title} />
                <Row label="Address" value={result.contact_address} />
                <Row label="Entity Type" value={result.entity_type} />
                <Row label="Entity Status" value={result.status} />
                <Row label="Source" value={result.source} />
                {result.notes && <Row label="Notes" value={result.notes} />}
              </div>

              <Button variant="outline" size="sm" onClick={handleLookup}>
                Search Again
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row items-center justify-between gap-2 sm:justify-between pt-4 border-t">
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            {result?.contact_name && (
              <Button size="sm" onClick={handleApply}>
                <CheckCircle className="h-4 w-4 mr-1" />
                Apply "{result.contact_name}" as Contact
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | null; highlight?: boolean }) {
  if (!value) return null;
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 px-3 py-2.5">
      <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</span>
      <span className={`break-words ${highlight ? 'font-bold' : ''}`}>{value}</span>
    </div>
  );
}
